import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import React from 'react';
import { API_URL } from '../../config';
import { ApiError, toApiError } from '../../lib/api-error';
import type { WorkflowTestExecutionDto } from './useWorkflowTestExecutions';
import type { WrongOrgInfo } from '../../ui/components/OrgSwitchPrompt';
import type { TestRunRerunAvailability } from './rerunAvailability';

// Test run status types
export type WorkflowTestRunStatus =
  | 'queued'
  | 'running'
  | 'waiting'
  | 'success'
  | 'failed'
  | 'inconclusive'
  | 'skipped'
  | 'flaky_success'
  | 'canceled'
  | 'timeout'
  | 'degraded';

// Test result types
export type AgenticTestResultDto = {
  id: string;
  testRunId: string;
  workflowId: string;
  nodeId: string | null;
  status: 'success' | 'failure' | 'inconclusive';
  message: string | null;
  issueId: string | null;
  metadata: unknown;
  createdAt: string;
};

// Test run with results
export type AgenticTestRunDto = {
  id: string;
  executionId: string | null;
  workflowId: string;
  status: WorkflowTestRunStatus;
  /** 1-based attempt number within (executionId, workflowId, scenarioId). */
  attempt: number;
  snapshotVersion: number;
  workflowRunId: string | null;
  triggeredByUserId: string | null;
  triggeredReason: string | null;
  linkedIssueId: string | null;
  isQuarantined: boolean;
  consecutiveFailures: number;
  failureStreakStartedAt: string | null;
  errorMessage: string | null;
  failureDiagnosticSummary: string | null;
  skipReason: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  results: AgenticTestResultDto[];
  // Joined from workflows table (may be added later)
  workflowName?: string;
  scenarioId: string | null;
  scenarioName: string | null;
  rerunAvailability: TestRunRerunAvailability;
};

export type AgenticTestRunAttemptDto = Pick<
  AgenticTestRunDto,
  'id' | 'attempt' | 'status' | 'startedAt' | 'finishedAt' | 'createdAt'
>;

type TestRunAttemptHistoryResponse = {
  ok: boolean;
  attempts: AgenticTestRunAttemptDto[];
  hasMore: boolean;
};

export async function fetchTestRunAttemptHistory(
  testRunId: string,
  fetchFn: FetchLike = fetch
): Promise<TestRunAttemptHistoryResponse> {
  const response = await fetchFn(`${API_URL}/workflows/test-runs/${testRunId}/attempt-history`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to load attempt history');
  }
  return response.json() as Promise<TestRunAttemptHistoryResponse>;
}

// API response type
type AgenticRunDetailResponse = {
  ok: boolean;
  execution: WorkflowTestExecutionDto;
  testRuns: AgenticTestRunDto[];
};

export function getAgenticTestRunFailureSubtext(
  run: Pick<AgenticTestRunDto, 'failureDiagnosticSummary' | 'errorMessage'>
): string | null {
  return run.failureDiagnosticSummary ?? run.errorMessage;
}

export type RerunAgenticWorkflowResponse = {
  ok: boolean;
  executionId: string;
};

type FetchLike = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1]
) => ReturnType<typeof fetch>;

export type ExecutionNotFoundRedirect =
  | {
      target: 'workflow_run';
      workflowRunId: string;
      testRunId?: string;
    }
  | {
      target: 'workflow_test_run';
      testRunId: string;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function getExecutionNotFoundRedirect(error: unknown): ExecutionNotFoundRedirect | null {
  if (!(error instanceof ApiError) || error.code !== 'EXECUTION_NOT_FOUND') return null;

  const redirect = error.details?.redirect;
  if (!isRecord(redirect)) return null;

  if (redirect.target === 'workflow_run' && typeof redirect.workflowRunId === 'string') {
    return {
      target: 'workflow_run',
      workflowRunId: redirect.workflowRunId,
      ...(typeof redirect.testRunId === 'string' ? { testRunId: redirect.testRunId } : {}),
    };
  }

  if (redirect.target === 'workflow_test_run' && typeof redirect.testRunId === 'string') {
    return {
      target: 'workflow_test_run',
      testRunId: redirect.testRunId,
    };
  }

  return null;
}

function getApiErrorCode(error: unknown): string {
  if (error instanceof ApiError) {
    const legacyError = error.details?.error;
    if (typeof legacyError === 'string' && legacyError.length > 0) return legacyError;
    if (error.code) return error.code;
    return error.message;
  }

  return error instanceof Error ? error.message : '';
}

export async function fetchAgenticRunDetail(
  executionId: string,
  fetchFn: FetchLike = fetch
): Promise<AgenticRunDetailResponse> {
  const response = await fetchFn(`${API_URL}/workflows/test-executions/${executionId}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    // Always surface the typed ApiError. Rewrapping non-redirect errors into a
    // plain Error dropped error.details, which regressed the EXECUTION_WRONG_ORG
    // org-switch prompt — it needs targetOrgId/targetOrgName off details. Retry
    // and redirect handling read the code via getApiErrorCode / the redirect
    // helper, both of which understand ApiError.
    throw await toApiError(response, 'Failed to load agentic run details');
  }

  return response.json() as Promise<AgenticRunDetailResponse>;
}

/**
 * Fetch agentic run execution details with test runs
 */
export function getAgenticRunDetailRefetchInterval(status: string | undefined): number | false {
  return status === 'running' || status === 'queued' ? 3000 : false;
}

export function useAgenticRunDetail(executionId: string | undefined) {
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<WrongOrgInfo | null>(null);
  const navigate = useNavigate();

  const query = useQuery<AgenticRunDetailResponse>({
    queryKey: ['agentic-run-detail', executionId],
    queryFn: async () => {
      try {
        const result = await fetchAgenticRunDetail(executionId!);
        setWrongOrgInfo(null);
        return result;
      } catch (error) {
        const details = error instanceof ApiError ? error.details : null;
        if (
          details?.error === 'EXECUTION_WRONG_ORG' &&
          typeof details.targetOrgId === 'string' &&
          typeof details.targetOrgName === 'string'
        ) {
          setWrongOrgInfo({
            orgId: details.targetOrgId,
            orgName: details.targetOrgName,
          });
        }
        throw error;
      }
    },
    enabled: !!executionId,
    // A just-started suite run creates its execution row asynchronously — the
    // orchestrator job inserts it a beat after POST /run returns — so the first
    // fetch can 404. Retry EXECUTION_NOT_FOUND for a bounded window (~15s) so
    // the page populates as soon as the row appears, instead of getting stuck.
    // Never retry a wrong-org error: that's terminal until the user switches
    // orgs, and retrying would keep re-triggering the org-switch prompt.
    retry: (failureCount, error) => {
      if (getExecutionNotFoundRedirect(error)) return false;
      const message = getApiErrorCode(error);
      if (message === 'EXECUTION_WRONG_ORG') return false;
      if (message === 'EXECUTION_NOT_FOUND') return failureCount < 10;
      return failureCount < 2;
    },
    retryDelay: 1500,
    refetchInterval: (query) => {
      const status = query.state.data?.execution?.status;
      return getAgenticRunDetailRefetchInterval(status);
    },
  });

  React.useEffect(() => {
    const redirect = getExecutionNotFoundRedirect(query.error);
    if (!redirect) return;

    if (redirect.target === 'workflow_run') {
      navigate({
        to: '/runs/$runId',
        params: { runId: redirect.workflowRunId },
        search: redirect.testRunId ? { testRunId: redirect.testRunId } : {},
        replace: true,
      });
      return;
    }

    navigate({
      to: '/runs/tests/$testRunId',
      params: { testRunId: redirect.testRunId },
      replace: true,
    });
  }, [navigate, query.error]);

  const clearWrongOrgInfo = React.useCallback(() => setWrongOrgInfo(null), []);

  return { ...query, wrongOrgInfo, clearWrongOrgInfo };
}

export function useTestRunAttemptHistory(testRunId: string) {
  return useQuery<TestRunAttemptHistoryResponse>({
    queryKey: ['test-run-attempt-history', testRunId],
    queryFn: () => fetchTestRunAttemptHistory(testRunId),
    staleTime: 30_000,
  });
}

// Timeline bucket for charts
type TimelineBucket = {
  bucketStart: string;
  label: string;
  passed: number;
  failed: number;
  hasData: boolean;
};

/**
 * Compute timeline buckets from test runs (client-side)
 */
function _useAgenticRunTimeline(
  testRuns: AgenticTestRunDto[],
  options?: { bucketMinutes?: number }
): { buckets: TimelineBucket[] } {
  const bucketMinutes = options?.bucketMinutes ?? 5;

  return React.useMemo(() => {
    // Filter to completed runs with finishedAt timestamps
    const completedRuns = testRuns.filter(
      (run) =>
        run.finishedAt &&
        (run.status === 'success' ||
          run.status === 'failed' ||
          run.status === 'inconclusive' ||
          run.status === 'flaky_success')
    );

    if (completedRuns.length === 0) {
      return { buckets: [] };
    }

    // Find time range
    const timestamps = completedRuns.map((run) => new Date(run.finishedAt!).getTime());
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);

    // Create buckets
    const bucketMs = bucketMinutes * 60 * 1000;
    const startBucket = Math.floor(minTime / bucketMs) * bucketMs;
    const endBucket = Math.ceil(maxTime / bucketMs) * bucketMs;

    const bucketMap = new Map<number, { passed: number; failed: number }>();

    // Initialize buckets
    for (let t = startBucket; t <= endBucket; t += bucketMs) {
      bucketMap.set(t, { passed: 0, failed: 0 });
    }

    // Count runs per bucket
    for (const run of completedRuns) {
      const time = new Date(run.finishedAt!).getTime();
      const bucket = Math.floor(time / bucketMs) * bucketMs;
      const current = bucketMap.get(bucket);
      if (current) {
        if (run.status === 'success' || run.status === 'flaky_success') {
          current.passed += 1;
        } else if (run.status === 'failed') {
          current.failed += 1;
        }
      }
    }

    // Convert to array
    const buckets: TimelineBucket[] = Array.from(bucketMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([timestamp, counts]) => {
        const date = new Date(timestamp);
        return {
          bucketStart: date.toISOString(),
          label: new Intl.DateTimeFormat(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          }).format(date),
          passed: counts.passed,
          failed: counts.failed,
          hasData: counts.passed > 0 || counts.failed > 0,
        };
      });

    return { buckets };
  }, [testRuns, bucketMinutes]);
}

/**
 * Rerun a single workflow test
 */
export async function rerunAgenticWorkflow(
  testRunId: string,
  fetchFn: FetchLike = fetch
): Promise<RerunAgenticWorkflowResponse> {
  const response = await fetchFn(`${API_URL}/workflows/test-runs/${testRunId}/rerun`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw await toApiError(response, 'Failed to rerun workflow');
  }
  return response.json() as Promise<RerunAgenticWorkflowResponse>;
}

export function useRerunAgenticWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      testRunId,
      executionId: _executionId,
    }: {
      testRunId: string;
      executionId: string;
    }) => {
      return rerunAgenticWorkflow(testRunId);
    },
    meta: { suppressToast: true },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-run-detail', variables.executionId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions'] });
      queryClient.invalidateQueries({ queryKey: ['test-run-detail'] });
    },
  });
}

type RerunTestExecutionResponse = {
  ok: boolean;
  suiteId: string | null;
  enqueued: number;
  skipped: number;
  startedAt?: string;
};

/**
 * Start a new suite execution from a prior execution's workflow set.
 */
export function useRerunTestExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sourceExecutionId,
    }: {
      sourceExecutionId: string;
    }): Promise<RerunTestExecutionResponse> => {
      const response = await fetch(
        `${API_URL}/workflows/test-executions/${sourceExecutionId}/rerun`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const data = await response.json().catch((): unknown => null);
        const error = data && typeof data === 'object' && 'error' in data ? data.error : null;
        throw new Error(
          typeof error === 'string' && error.length > 0 ? error : 'Failed to start test run'
        );
      }
      return response.json() as Promise<RerunTestExecutionResponse>;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['agentic-run-detail', variables.sourceExecutionId],
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions'] });
    },
  });
}

type RetryFailedTestExecutionResponse = {
  ok: boolean;
  data: {
    executionId: string;
    retriedCount: number;
    attempt: number;
  };
};

const RETRY_FAILED_FALLBACK = 'We couldn’t retry the failed workflows. Please try again.';

/**
 * Keep framework-level 5xx responses from leaking into the retry toast while
 * preserving specific API errors that can help the user recover.
 */
export async function toRetryFailedError(response: Response): Promise<ApiError> {
  const error = await toApiError(response, RETRY_FAILED_FALLBACK);
  if (error.status < 500 || error.message.trim().toLowerCase() !== 'internal server error') {
    return error;
  }

  return new ApiError({
    message: RETRY_FAILED_FALLBACK,
    status: error.status,
    userError: error.userError,
    code: error.code,
    details: error.details,
  });
}

/**
 * Retry only the failed/timeout/inconclusive workflows of a terminal suite
 * execution, appending new attempt-N rows to the SAME execution.
 */
export function useRetryFailedTestExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (executionId: string): Promise<RetryFailedTestExecutionResponse> => {
      const response = await fetch(
        `${API_URL}/workflows/test-executions/${executionId}/retry-failed`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!response.ok) {
        throw await toRetryFailedError(response);
      }
      return response.json() as Promise<RetryFailedTestExecutionResponse>;
    },
    onSuccess: (_data, executionId) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-run-detail', executionId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions'] });
    },
  });
}

/**
 * Cancel a running test execution
 */
export function useCancelTestExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      executionId: string
    ): Promise<{ ok: boolean; alreadyTerminal?: boolean }> => {
      const response = await fetch(`${API_URL}/workflows/test-executions/${executionId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to cancel execution');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentic-run-detail'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions'] });
    },
  });
}

/**
 * Delete a test execution
 */
export function useDeleteTestExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (executionId: string): Promise<{ ok: boolean }> => {
      const response = await fetch(`${API_URL}/workflows/test-executions/${executionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete execution');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agentic-run-detail'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-test-executions'] });
    },
  });
}

/**
 * Compute pass rate from execution data
 */
function _computePassRate(execution: WorkflowTestExecutionDto | undefined): number {
  if (!execution) return 0;
  const total = execution.successfulWorkflows + execution.failedWorkflows;
  if (total === 0) return 0;
  return Math.round((execution.successfulWorkflows / total) * 100);
}

/**
 * Format duration from timestamps
 */
export function formatDuration(
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
}

/**
 * Format relative time ago
 */
export function formatRelativeTimeAgo(value?: string | null): string {
  if (!value) return 'Unknown';
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown';

  const deltaMs = Date.now() - timestamp;
  const deltaSeconds = Math.floor(Math.abs(deltaMs) / 1000);
  const future = deltaMs < 0;

  if (deltaSeconds < 10) return future ? 'in a few seconds' : 'just now';
  if (deltaSeconds < 60) return future ? `in ${deltaSeconds}s` : `${deltaSeconds}s ago`;

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return future ? `in ${deltaMinutes}m` : `${deltaMinutes}m ago`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return future ? `in ${deltaHours}h` : `${deltaHours}h ago`;

  const deltaDays = Math.floor(deltaHours / 24);
  return future ? `in ${deltaDays}d` : `${deltaDays}d ago`;
}
